'use client'

import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  Fragment,
} from 'react'
import * as m from 'motion/react-m'
import { Motion } from '@/lib/motion/lazy-motion'
import Hero from '@/components/content/common/Hero'
import CompareSelect, {
  type AppMenusProps,
} from '@/components/content/comparisons/compare/CompareSelect'
import Container from '@/components/structure/Container'
import AppIcon from '@/components/content/app/AppIcon'
import AppStack from '@/components/content/app/AppStack'
import EfficiencyPoints from '@/components/ui/EfficiencyPoints'
import Timestamp from '@/components/ui/Timestamp'
import { generateMenu } from '@/helpers/menus'
import { cn, hexToRgba, getBrandColors } from '@/lib/utils'
import { extractCriteriaForCategory, checkAppBranding } from '@/lib/formatting'
import { generateCriteriaList } from '@/components/content/comparisons/helpers'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import type { MenuItem } from '@/helpers/menus'
import type { CriterionWithRating } from '@/components/ui/CriteriaOverview'
import type { App, Category, Criterion } from '@/payload-types'
import type { Timestamps } from '@/types/app'

type CompareHeroAppStackPointsProps = {
  app: App
  index: number
  isDirectComparison: boolean
  comparisonAppCount: number
  appCategoryEfficiencyPoints?: number
  allAppsHaveEfficiencyPoints: boolean
  efficiencyPointsLoading?: boolean
  criteria: CriterionWithRating[]
  classes: {
    gap: string
    size: string
    visibility: string
  }
}

function CompareHeroAppStackPoints({
  app,
  index,
  isDirectComparison,
  comparisonAppCount,
  appCategoryEfficiencyPoints,
  allAppsHaveEfficiencyPoints,
  efficiencyPointsLoading,
  criteria,
  classes,
}: CompareHeroAppStackPointsProps) {
  const { backgroundColor } = getBrandColors(app)
  const { appGlyph, appIcon } = checkAppBranding(app)

  // Points render before the stack for every app except the first, which
  // mirrors them on the right (see the original left/right `align` split).
  const pointsBeforeStack = index !== 0

  // `efficiencyPointsLoading` is false on a plain SSG page load (the score
  // is already known server-side, so nothing is actually "loading") — but we
  // still want every page load to start covered and play the reveal, so the
  // very first render ignores the incoming props and forces covering. This
  // also happens to be true during SSR (no `useEffect` runs there), so the
  // server-rendered HTML itself starts in the covered state.
  const [hasMounted, setHasMounted] = useState(false)
  useEffect(() => {
    setHasMounted(true)
  }, [])

  // Loaded apps without applicable ratings reveal the existing dash too.
  const isCovering =
    !hasMounted ||
    !!efficiencyPointsLoading ||
    !allAppsHaveEfficiencyPoints

  // What EfficiencyPoints actually renders is a held snapshot, not the live
  // props — otherwise it would snap to "-"/the loading skeleton the instant
  // an app swap starts, well before the AppStack has covered it. Instead:
  // while covering, keep showing the previous snapshot (faded out below)
  // until the stack has had time to finish sliding into place, then swap to
  // whatever's current
  const [displayed, setDisplayed] = useState({
    app,
    appCategoryEfficiencyPoints,
    criteria,
    loading: !!efficiencyPointsLoading,
  })
  useEffect(() => {
    const next = { app, appCategoryEfficiencyPoints, criteria, loading: !!efficiencyPointsLoading }
    if (!isCovering) {
      setDisplayed(next)
      return
    }
    const timeout = setTimeout(() => setDisplayed(next), 300)
    return () => clearTimeout(timeout)
  }, [isCovering, app, appCategoryEfficiencyPoints, criteria, efficiencyPointsLoading])

  const pointsRef = useRef<HTMLDivElement>(null)
  const stackRef = useRef<HTMLDivElement>(null)
  const [coverOffset, setCoverOffset] = useState<number | null>(null)

  useLayoutEffect(() => {
    if (!isDirectComparison) return
    const pointsEl = pointsRef.current
    const stackEl = stackRef.current
    if (!pointsEl || !stackEl) return

    const measure = () => {
      const offset = pointsBeforeStack
        ? pointsEl.offsetLeft - stackEl.offsetLeft
        : pointsEl.offsetLeft + pointsEl.offsetWidth - (stackEl.offsetLeft + stackEl.offsetWidth)
      setCoverOffset(offset)
    }

    measure()

    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(pointsEl)
    observer.observe(stackEl)
    window.addEventListener('resize', measure)

    return () => {
      observer.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [isDirectComparison, pointsBeforeStack])

  const rotateDeg = index % 2 === 0 ? -3 : 3
  const roughCover = pointsBeforeStack ? 'max(-100%, -150px)' : 'min(100%, 150px)'
  const coverTransform = coverOffset !== null ? `${coverOffset}px` : roughCover
  const stackStyle = isDirectComparison
    ? { rotate: `${rotateDeg}deg`, translate: isCovering ? coverTransform : '0px' }
    : undefined

  const pointsSlot = (align: 'left' | 'right') => (
    <div
      ref={pointsRef}
      className={cn(
        'hidden shrink-0 transition-opacity duration-150 sm:block',
        isCovering ? 'opacity-0' : 'opacity-100',
      )}
    >
      <EfficiencyPoints
        app={displayed.app}
        criteria={displayed.criteria}
        appCategoryEfficiencyPoints={displayed.appCategoryEfficiencyPoints}
        loading={displayed.loading}
        align={align}
        alwaysShow
      />
    </div>
  )

  const appStack = (
    <AppStack
      ref={isDirectComparison ? stackRef : undefined}
      icon={appIcon}
      glyph={appGlyph}
      name={app.name}
      slug={app.slug}
      multiple={true}
      background={backgroundColor}
      invert={true}
      size={'fluid'}
      priority={true}
      style={stackStyle}
      className={cn(
        'aspect-square w-full',
        classes.size,
        !isDirectComparison && (index % 2 === 0 ? '-rotate-3' : 'rotate-3'),
        isDirectComparison && 'z-10 [@media(max-width:639px)]:![translate:none]',
        isDirectComparison &&
          coverOffset !== null &&
          'transition-[translate] duration-300 ease-in-out',
      )}
    />
  )

  return (
    <Fragment>
      {isDirectComparison && pointsBeforeStack && pointsSlot('left')}
      {appStack}
      {isDirectComparison && !pointsBeforeStack && pointsSlot('right')}
      {index < comparisonAppCount - 1 && (
        <span
          className={cn(
            classes.visibility,
            'h-8 w-12 shrink-0 items-center justify-center rounded-full border border-white/20 bg-white/5 font-semibold uppercase text-white',
          )}
        >
          vs
        </span>
      )}
    </Fragment>
  )
}

type CompareHeroProps = {
  apps: App[]
  appCategoryEfficiencyPoints: Record<App['id'], number | undefined>
  appCategoryCriteriaWithRatings: Record<App['id'], CriterionWithRating[]>
  efficiencyPointsLoading?: boolean
  relatedApps: App[]
  setAppsAction: Dispatch<SetStateAction<App[]>>
  menu: MenuItem[]
  categories: Array<{ id: Category['id'] }>
  timestamps: Timestamps
  isSingleAppSelection?: boolean
}

export default function CompareHero({
  apps,
  appCategoryEfficiencyPoints,
  appCategoryCriteriaWithRatings,
  efficiencyPointsLoading,
  relatedApps,
  setAppsAction,
  menu,
  categories,
  timestamps,
  isSingleAppSelection = false,
}: CompareHeroProps) {
  const { sm } = useBreakpoint()
  const [appsToShow, setAppsToShow] = useState<number>(apps.length)

  const isDirectComparison = apps.length === 2

  const [settledEfficiencyPointsLoading, setSettledEfficiencyPointsLoading] =
    useState(!!efficiencyPointsLoading)
  useEffect(() => {
    if (efficiencyPointsLoading) {
      setSettledEfficiencyPointsLoading(true)
      return
    }
    const timeout = setTimeout(() => setSettledEfficiencyPointsLoading(false), 200)
    return () => clearTimeout(timeout)
  }, [efficiencyPointsLoading])

  // Single-app picker: the empty second box lists comparable apps as links to
  // /compare/<a>-vs-<b>, so choosing one is a normal navigation that runs the
  // server's canonical-ordering and overlap gates.
  const secondBoxMenu = useMemo<MenuItem[]>(() => {
    if (!isSingleAppSelection) return []
    const [appA] = apps
    if (!appA?.slug) return []
    return relatedApps
      .filter((related) => related.slug && related.id !== appA.id)
      .map((related) => {
        const { appIcon, appGlyph } = checkAppBranding(related)
        const { backgroundColor } = getBrandColors(related)
        return {
          label: related.name,
          icon: (
            <AppIcon
              icon={appIcon}
              glyph={appGlyph}
              name={related.name}
              background={backgroundColor}
              size={20}
            />
          ),
          link: `/compare/${appA.slug}-vs-${related.slug}`,
        }
      })
  }, [isSingleAppSelection, apps, relatedApps])

  const gradient = useMemo(
    () => `
    linear-gradient(to top, rgba(0,0,0,1), rgba(0,0,0,0)),
    linear-gradient(to right, ${apps
      .map((app) => {
        const { primaryColor } = getBrandColors(app)
        return hexToRgba(primaryColor, 0.25)
      })
      .join(', ')}),
    #000
  `,
    [apps],
  )

  const appMenus = useMemo(() => {
    return apps.reduce((map: AppMenusProps, app) => {
      map[app.id] = generateMenu('compare', {
        apps: [app],
        relatedApps: relatedApps,
        setApps: setAppsAction,
        options: { isSelectable: true, isShareable: true },
        comparison: apps,
      })
      return map
    }, {})
  }, [apps, relatedApps, setAppsAction])

  const moreAppsMenu = useMemo(() => {
    return generateMenu('compare', {
      apps: apps.slice(appsToShow),
      relatedApps: relatedApps,
      setApps: setAppsAction,
      options: { isSwappable: true },
      comparison: apps,
    })
  }, [apps, appsToShow, relatedApps, setAppsAction])

  // A present key with an undefined score means loaded but unrated.
  const allAppsHaveEfficiencyPoints = useMemo(
    () =>
      !efficiencyPointsLoading &&
      apps.every((app) => Object.hasOwn(appCategoryEfficiencyPoints, app.id)),
    [apps, appCategoryEfficiencyPoints, efficiencyPointsLoading],
  )

  const primaryCategoryId = categories[0]?.id
  const criteria = useMemo<Criterion[]>(
    () =>
      primaryCategoryId ? extractCriteriaForCategory(apps, primaryCategoryId).slice(0, 5) : [],
    [apps, primaryCategoryId],
  ) satisfies Criterion[]

  const classes = useMemo(() => {
    const appCount = apps.length
    if (appCount > 3) {
      return {
        gap: 'gap-2 xs:gap-8 md:gap-14 lg:gap-10',
        size: 'max-w-[130px] md:max-w-[220px]',
        visibility: 'hidden lg:flex',
      }
    } else if (appCount > 2) {
      return {
        gap: 'gap-6 md:gap-10 lg:gap-16',
        size: 'max-w-[130px] md:max-w-[220px]',
        visibility: 'hidden sm:flex',
      }
    } else {
      return {
        gap: 'gap-12 md:gap-14 lg:gap-20',
        size: 'max-w-[130px] sm:max-w-[240px]',
        visibility: 'hidden xs:flex',
      }
    }
  }, [apps.length])

  const TitleTag = isDirectComparison && !sm ? 'h1' : 'span'
  const SubtitleTag = !isDirectComparison && !sm ? 'h1' : 'span'

  return (
    <Hero gradient={gradient} className="pt-28 md:pt-16" height={{ condensed: true }}>
      {({ animations }) => (
        <Motion>
          <>
            <Container className="relative z-30 flex h-3/4 items-center md:h-full md:-translate-y-10 md:pt-0">
              <m.div
                style={animations}
                className="mx-auto flex h-fit w-full flex-col items-center gap-5 md:gap-8"
              >
                <div className="flex max-w-screen-lg flex-col items-center gap-3 md:gap-5">
                  {!isDirectComparison && (
                    <SubtitleTag className="mb-2.5 flex items-center font-sans text-10 font-medium uppercase tracking-widest text-white/40 sm:hidden">
                      {apps.map((app) => app.name).join(' vs ')}
                    </SubtitleTag>
                  )}
                  <TitleTag className="text-center font-heading text-36 font-bold md:text-64">
                    {isDirectComparison ? apps.map((app) => app.name).join(' vs ') : 'Compare Apps'}
                  </TitleTag>
                  {(timestamps?.modified || timestamps?.published) && (
                    <Timestamp
                      modified={timestamps.modified}
                      published={timestamps.published}
                      lead="Updated"
                      className="sm:hidden"
                    />
                  )}
                  {criteria && criteria.length > 0 && (
                    <p className="mx-auto max-w-screen-md text-balance text-center text-18 text-white/60">
                      Efficient at {generateCriteriaList(criteria)}
                    </p>
                  )}
                </div>

                <CompareSelect
                  apps={apps}
                  menu={menu}
                  appMenus={appMenus}
                  moreAppsMenu={moreAppsMenu}
                  appsToShow={appsToShow}
                  setAppsToShow={setAppsToShow}
                  isSingleAppSelection={isSingleAppSelection}
                  secondBoxMenu={secondBoxMenu}
                />
              </m.div>
            </Container>

            <m.div
              className={cn(
                'absolute -bottom-4 z-20 md:-bottom-8',
                apps.length > 3 ? '-left-[25px] w-[calc(100%+50px)] md:left-0 md:w-full' : 'w-full',
              )}
              style={animations}
            >
              <Container className={cn(classes.gap, 'flex w-full items-center justify-center')}>
                {apps.map((app, index) => {
                  // Don't default to 0 — undefined renders a dash in EfficiencyPoints
                  const appPoints = appCategoryEfficiencyPoints[app.id]

                  return (
                    <CompareHeroAppStackPoints
                      key={index}
                      app={app}
                      criteria={appCategoryCriteriaWithRatings[app.id] ?? criteria}
                      appCategoryEfficiencyPoints={appPoints}
                      allAppsHaveEfficiencyPoints={allAppsHaveEfficiencyPoints}
                      efficiencyPointsLoading={settledEfficiencyPointsLoading}
                      index={index}
                      isDirectComparison={isDirectComparison}
                      comparisonAppCount={apps.length}
                      classes={classes}
                    />
                  )
                })}
              </Container>
            </m.div>
          </>
        </Motion>
      )}
    </Hero>
  )
}
